import { type ChangeEvent, useEffect, useRef, useState } from 'react';

import type { FieldDefinition, ValidationIssue } from '@inside-us-aos/domain';

import styles from './DateInput.module.css';
import { issueMessage } from './issueMessage';

export interface DateInputProps {
  field: FieldDefinition;
  /**
   * Canonical engine value — ISO 8601 (`YYYY-MM-DD`) when complete,
   * `""` otherwise. The catalog stores `valueType: 'date'` answers in
   * this format and the PDF projector reads it directly.
   */
  value: string;
  issues: readonly ValidationIssue[];
  onChange: (next: string) => void;
  /**
   * Whether the user has interacted with the field. When `false`,
   * issues are suppressed. Defaults to `true` for backward
   * compatibility with existing call-sites.
   */
  touched?: boolean;
  /** Fires on the input's native `onBlur`. */
  onBlur?: () => void;
}

const ISO_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;

function isoToDisplay(iso: string): string {
  if (iso === '') return '';
  const m = iso.match(ISO_REGEX);
  if (!m) return '';
  return `${m[2]}/${m[3]}/${m[1]}`;
}

function displayToIso(display: string): string | null {
  const m = display.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const month = Number(m[1]);
  const day = Number(m[2]);
  const year = Number(m[3]);
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  if (year < 1000) return null;
  return `${m[3]}-${m[1]}-${m[2]}`;
}

function maskDigits(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/**
 * Masked MM/DD/YYYY date primitive.
 *
 * Replaces the native `<input type="date">` whose segment auto-advance
 * behavior is browser-dependent — Chrome/Safari often refused to move
 * focus from the month segment to the day segment after a 2-digit
 * value, leaving users typing `0112` and watching it become `12` on
 * the month. The masked text input owns the segment transitions
 * directly: digits flow into the MM/DD/YYYY positions, slashes are
 * auto-inserted, and the cursor advances naturally.
 *
 * Storage is unchanged: the engine still receives ISO 8601
 * (`YYYY-MM-DD`) strings via `onChange`. Partial typing emits `""` so
 * the engine never holds an incomplete value.
 */
export function DateInput({
  field,
  value,
  issues,
  onChange,
  touched = true,
  onBlur,
}: DateInputProps): JSX.Element {
  const inputId = `field-${field.id.replace(/\./g, '-')}`;
  const errorListId = `${inputId}-errors`;
  const showIssues = touched && issues.length > 0;
  const inputClassName = showIssues
    ? `${styles.field__input} ${styles['field__input--invalid']}`
    : styles.field__input;

  // Local display state owns the user's typing — partial like
  // "01/22/199" never round-trips through the engine. The effect
  // below syncs FROM `value` when the engine state changes externally
  // (hydrate, programmatic prefill) without clobbering mid-typing.
  const [display, setDisplay] = useState<string>(() => isoToDisplay(value));
  const focusedRef = useRef(false);

  useEffect(() => {
    // Skip resync while the user is actively typing — the input is
    // the source of truth in that window.
    if (focusedRef.current) return;
    const next = isoToDisplay(value);
    setDisplay(prev => (prev === next ? prev : next));
  }, [value]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const next = maskDigits(event.target.value);
    setDisplay(next);
    const iso = displayToIso(next);
    onChange(iso !== null ? iso : '');
  };

  return (
    <div className={styles.field} data-field-id={field.id}>
      <label htmlFor={inputId} className={styles.field__label}>
        {field.label}
        {field.required === true ? (
          <span className={styles['field__required-mark']}>*</span>
        ) : null}
      </label>

      <input
        id={inputId}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="MM/DD/YYYY"
        maxLength={10}
        value={display}
        onChange={handleChange}
        onFocus={() => {
          focusedRef.current = true;
        }}
        onBlur={() => {
          focusedRef.current = false;
          onBlur?.();
        }}
        aria-required={field.required === true || undefined}
        aria-invalid={showIssues || undefined}
        aria-describedby={showIssues ? errorListId : undefined}
        className={inputClassName}
      />

      {field.helpText !== undefined && field.helpText !== '' ? (
        <p className={styles['field__help-text']}>{field.helpText}</p>
      ) : null}

      {showIssues ? (
        <div role="alert">
          <ul id={errorListId} className={styles['field__error-list']}>
            {issues.map((issue, index) => (
              <li key={`${issue.code}-${index}`}>
                <span aria-hidden="true" className={styles['field__error-icon']}>
                  !
                </span>
                {issueMessage(issue)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
